import { Routes, Route, Navigate } from "react-router-dom";
import {
  Layout,
  NewDeliveryForm,
  NewMassiveDeliveryForm,
  NewMassiveSuppliersForm,
  NewSiteForm,
  NewSupplierForm,
  NewUserForm,
} from "../../../components";
import {
  Home,
  MaterialCnt,
  NotFound,
  CreateBuyerMaterial,
  EditMaterial,
  EditSite,
  Materials,
  Profile,
  Orders,
  Order,
  OrderByRequest,
  Sites,
  User,
  Users,
  Roles,
  ValidateUser,
  EditUser,
  JointBuyerBid,
  BuyerBid,
  Chat,
  Suppliers,
  Supplier,
  Tags,
  Deliveries,
  Delivery,
  DelegateCnt,
  PurchaseRequestImport,
  PurchaseRequestV2Detail,
  PurchaseRequestsV2,
  NewPurchaseRequestV2,
  ProcurementAnalytics,
  PurchaseOrdersPage,
  CoordinateOcPage,
  ApprovalRulesPage,
  OcSettingsPage,
  TokenAcceptPage,
  CompanyReviewPage,
} from "../../../containers";
import {
  EntityIdRedirect,
  LegacyPurchaseRequestRedirect,
  OrderIdQueryRedirect,
  QueryPreservingRedirect,
} from "../../redirects";
import RequireAbility from "../../guards/RequireAbility";
import RequirePurchaseOrderModule from "../../guards/RequirePurchaseOrderModule";
import { ABILITIES } from "../../../utils";
import { useUser } from "../../../context";

// The single buyer route tree. What a user can reach is decided by their effective abilities
// (`useUser().can`), not by a per-role tree: each screen route is wrapped in <RequireAbility>,
// which redirects (never renders) when the ability is missing. Legacy path normalisers, the
// index, /profile and /validate-user stay ungated.
//
// Exposed as a plain builder so structural tests can walk the <Route> tree without rendering
// (the component itself needs the user context for the index route).
export const buildBuyerRoutes = (
  isRequester: boolean,
  can: (ability: string) => boolean
) => (
  <Routes>
    <Route path="/validate-user" element={<ValidateUser />} />
    {/* Public emailed-OC link: resolves for a logged-in user too, outside the app shell. */}
    <Route path="/purchase-orders/token/:token" element={<TokenAcceptPage />} />
    <Route path="/company-review/:token" element={<CompanyReviewPage />} />
    <Route element={<Layout />}>
      {/* A requester's home is the PR list; everyone else gets the dashboard. A UX landing
          choice (Home is not sensitive), not an access check. The redirect also requires
          VIEW_PURCHASE_REQUESTS: /purchase-requests is gated on it and bounces to "/", so a
          requester without it would otherwise loop between the two. */}
      <Route
        path="/"
        element={
          isRequester && can(ABILITIES.VIEW_PURCHASE_REQUESTS) ? (
            <Navigate to="/purchase-requests" replace />
          ) : (
            <Home />
          )
        }
      />
      <Route path="/profile" element={<Profile />} />

      <Route
        path="/materials"
        element={
          <RequireAbility ability={ABILITIES.VIEW_MATERIALS}>
            <Materials />
          </RequireAbility>
        }
      />
      <Route
        path="/materials/:id"
        element={
          <RequireAbility ability={ABILITIES.VIEW_MATERIALS}>
            <MaterialCnt />
          </RequireAbility>
        }
      />
      <Route
        path="/materials/:id/edit"
        element={
          <RequireAbility ability={ABILITIES.EDIT_MATERIALS}>
            <EditMaterial />
          </RequireAbility>
        }
      />
      <Route
        path="/materials/new"
        element={
          <RequireAbility ability={ABILITIES.CREATE_MATERIALS}>
            <CreateBuyerMaterial />
          </RequireAbility>
        }
      />

      <Route
        path="/purchase-requests"
        element={
          <RequireAbility ability={ABILITIES.VIEW_PURCHASE_REQUESTS}>
            <PurchaseRequestsV2 />
          </RequireAbility>
        }
      />
      <Route
        path="/purchase-requests/import"
        element={
          <RequireAbility ability={ABILITIES.CREATE_PURCHASE_REQUESTS}>
            <PurchaseRequestImport />
          </RequireAbility>
        }
      />
      <Route
        path="/purchase-requests/new"
        element={
          <RequireAbility ability={ABILITIES.CREATE_PURCHASE_REQUESTS}>
            <NewPurchaseRequestV2 />
          </RequireAbility>
        }
      />
      <Route
        path="/purchase-requests/:id"
        element={
          <RequireAbility ability={ABILITIES.VIEW_PURCHASE_REQUESTS}>
            <PurchaseRequestV2Detail />
          </RequireAbility>
        }
      />

      <Route
        path="/orders"
        element={
          <RequireAbility ability={ABILITIES.VIEW_ORDERS}>
            <Orders />
          </RequireAbility>
        }
      />
      <Route
        path="/orders/new"
        element={
          <RequireAbility ability={ABILITIES.CREATE_ORDERS}>
            <Order />
          </RequireAbility>
        }
      />
      <Route
        path="/orders/from-request"
        element={
          <RequireAbility ability={ABILITIES.CREATE_ORDERS}>
            <OrderByRequest />
          </RequireAbility>
        }
      />
      <Route
        path="/orders/multiple/:id"
        element={
          <RequireAbility ability={ABILITIES.VIEW_ORDERS}>
            <JointBuyerBid />
          </RequireAbility>
        }
      />
      <Route
        path="/orders/:id"
        element={
          <RequireAbility ability={ABILITIES.VIEW_ORDERS}>
            <BuyerBid />
          </RequireAbility>
        }
      />

      <Route
        path="/deliveries"
        element={
          <RequireAbility ability={ABILITIES.VIEW_DELIVERIES}>
            <Deliveries />
          </RequireAbility>
        }
      />
      <Route
        path="/deliveries/new"
        element={
          <RequireAbility ability={ABILITIES.MANAGE_DELIVERIES}>
            <NewDeliveryForm />
          </RequireAbility>
        }
      />
      <Route
        path="/deliveries/bulk"
        element={
          <RequireAbility ability={ABILITIES.MANAGE_DELIVERIES}>
            <NewMassiveDeliveryForm />
          </RequireAbility>
        }
      />
      <Route
        path="/deliveries/:id"
        element={
          <RequireAbility ability={ABILITIES.VIEW_DELIVERIES}>
            <Delivery />
          </RequireAbility>
        }
      />

      <Route
        path="/suppliers"
        element={
          <RequireAbility ability={ABILITIES.VIEW_SUPPLIERS}>
            <Suppliers />
          </RequireAbility>
        }
      />
      <Route
        path="/suppliers/new"
        element={
          <RequireAbility ability={ABILITIES.MANAGE_SUPPLIERS}>
            <NewSupplierForm />
          </RequireAbility>
        }
      />
      <Route
        path="/suppliers/upload"
        element={
          <RequireAbility ability={ABILITIES.MANAGE_SUPPLIERS}>
            <NewMassiveSuppliersForm />
          </RequireAbility>
        }
      />
      <Route
        path="/suppliers/:id"
        element={
          <RequireAbility ability={ABILITIES.VIEW_SUPPLIERS}>
            <Supplier />
          </RequireAbility>
        }
      />

      <Route
        path="/users"
        element={
          <RequireAbility ability={ABILITIES.VIEW_USERS}>
            <Users />
          </RequireAbility>
        }
      />
      {/* A static "/users/new" always outranks the dynamic "/users/:id", so a role without
          MANAGE_USERS (e.g. Visualizer) is sent to the list rather than falling through to a
          User detail screen looking up a user literally called "new". */}
      <Route
        path="/users/new"
        element={
          <RequireAbility ability={ABILITIES.MANAGE_USERS} fallback="/users">
            <NewUserForm />
          </RequireAbility>
        }
      />
      <Route
        path="/users/:id/edit"
        element={
          <RequireAbility ability={ABILITIES.MANAGE_USERS}>
            <EditUser />
          </RequireAbility>
        }
      />
      <Route
        path="/users/:id"
        element={
          <RequireAbility ability={ABILITIES.VIEW_USERS}>
            <User />
          </RequireAbility>
        }
      />

      <Route
        path="/roles"
        element={
          <RequireAbility ability={ABILITIES.MANAGE_ROLES}>
            <Roles />
          </RequireAbility>
        }
      />

      <Route
        path="/sites"
        element={
          <RequireAbility ability={ABILITIES.VIEW_SITES}>
            <Sites />
          </RequireAbility>
        }
      />
      <Route
        path="/sites/new"
        element={
          <RequireAbility ability={ABILITIES.MANAGE_SITES}>
            <NewSiteForm />
          </RequireAbility>
        }
      />
      <Route
        path="/sites/:id"
        element={
          <RequireAbility ability={ABILITIES.MANAGE_SITES}>
            <EditSite />
          </RequireAbility>
        }
      />

      <Route
        path="/tags"
        element={
          <RequireAbility ability={ABILITIES.VIEW_TAGS}>
            <Tags />
          </RequireAbility>
        }
      />
      <Route
        path="/analytics"
        element={
          <RequireAbility ability={ABILITIES.VIEW_ANALYTICS}>
            <ProcurementAnalytics />
          </RequireAbility>
        }
      />
      {/* OC (Purchase Orders) is ability-gated AND a per-company module opt-in, so the route
          composes both guards; the nav item applies the same two checks (see Layout). */}
      <Route
        path="/purchase-orders"
        element={
          <RequirePurchaseOrderModule>
            <RequireAbility ability={ABILITIES.VIEW_PURCHASE_ORDERS}>
              <PurchaseOrdersPage />
            </RequireAbility>
          </RequirePurchaseOrderModule>
        }
      />
      {/* Static, so it outranks "/purchase-orders/:id". Gated by its OWN ability (not VIEW). */}
      <Route
        path="/purchase-orders/approval-rules"
        element={
          <RequirePurchaseOrderModule>
            <RequireAbility ability={ABILITIES.MANAGE_PURCHASE_ORDER_APPROVALS}>
              <ApprovalRulesPage />
            </RequireAbility>
          </RequirePurchaseOrderModule>
        }
      />
      <Route
        path="/purchase-orders/settings"
        element={
          <RequirePurchaseOrderModule>
            <RequireAbility ability={ABILITIES.MANAGE_PURCHASE_ORDER_APPROVALS}>
              <OcSettingsPage />
            </RequireAbility>
          </RequirePurchaseOrderModule>
        }
      />
      {/* Static, so it outranks "/purchase-orders/:id". Coordinate is a create-OC capability. */}
      <Route
        path="/purchase-orders/coordinate"
        element={
          <RequirePurchaseOrderModule>
            <RequireAbility ability={ABILITIES.CREATE_PURCHASE_ORDERS}>
              <CoordinateOcPage />
            </RequireAbility>
          </RequirePurchaseOrderModule>
        }
      />
      <Route
        path="/purchase-orders/:id"
        element={
          <RequirePurchaseOrderModule>
            <RequireAbility ability={ABILITIES.VIEW_PURCHASE_ORDERS}>
              <PurchaseOrdersPage />
            </RequireAbility>
          </RequirePurchaseOrderModule>
        }
      />
      <Route
        path="/chat"
        element={
          <RequireAbility ability={ABILITIES.VIEW_CHAT}>
            <Chat />
          </RequireAbility>
        }
      />
      <Route
        path="/delegate"
        element={
          <RequireAbility ability={ABILITIES.VIEW_SUPPLIERS}>
            <DelegateCnt />
          </RequireAbility>
        }
      />

      {/* Legacy path normalisers — ungated on purpose: they only rewrite the path, and the
          canonical route they land on applies its own ability gate. Kept so no bookmarked URL
          regresses. */}
      <Route
        path="/material/:id"
        element={<EntityIdRedirect to={(id) => `/materials/${id}`} />}
      />
      <Route
        path="/material/edit/:id"
        element={<EntityIdRedirect to={(id) => `/materials/${id}/edit`} />}
      />
      <Route
        path="/material/new"
        element={<Navigate to="/materials/new" replace />}
      />
      <Route
        path="/user/material/:id"
        element={<EntityIdRedirect to={(id) => `/materials/${id}`} />}
      />
      <Route
        path="/user/edit/:id"
        element={<EntityIdRedirect to={(id) => `/users/${id}/edit`} />}
      />
      <Route
        path="/user/:id"
        element={<EntityIdRedirect to={(id) => `/users/${id}`} />}
      />
      <Route
        path="/site/:id"
        element={<EntityIdRedirect to={(id) => `/sites/${id}`} />}
      />
      <Route path="/site/new" element={<Navigate to="/sites/new" replace />} />
      <Route
        path="/order"
        element={<QueryPreservingRedirect to="/orders/new" />}
      />
      <Route path="/order/multiple/:id" element={<OrderIdQueryRedirect />} />
      <Route
        path="/order/:id"
        element={<EntityIdRedirect to={(id) => `/orders/${id}`} />}
      />
      <Route
        path="/order-by-request"
        element={<QueryPreservingRedirect to="/orders/from-request" />}
      />
      <Route
        path="/purchase-requests-v2/:id"
        element={<LegacyPurchaseRequestRedirect />}
      />
      <Route
        path="/new-purchase-request"
        element={<Navigate to="/purchase-requests/new" replace />}
      />

      <Route path="*" element={<NotFound />} />
    </Route>
  </Routes>
);

const UnifiedBuyerRoutes = () => {
  const { isRequester, can } = useUser();
  return buildBuyerRoutes(isRequester, can);
};

export default UnifiedBuyerRoutes;
